import { Measure } from '@e2m-customer-portal/shared';
import { ReactElement, useRef } from 'react';
import { useTranslation } from 'react-i18next';

function alignToPowerLevel(value: number, powerLevels: number[], installedPower: number, step?: number) {
  const levels = [0, ...powerLevels, installedPower];

  for (let level = 1; level < levels.length; level++) {
    const higherLevel = levels[level];
    const lowerLevel = levels[level - 1];

    // Slider alignment
    if (step === undefined) {
      if (value < lowerLevel + (higherLevel - lowerLevel) / 2) return lowerLevel;
      else if (value < higherLevel) return higherLevel;
    }
    // Stepper alignment
    else if (step < 0 && value < higherLevel) return lowerLevel;
    else if (step > 0 && value < higherLevel) return higherLevel;
  }

  return value;
}

interface ValuePickerMobileProps {
  production: number;
  installedPower: number;
  powerLevels?: number[];
  selectValue: (value: Measure) => void;
}

export function ValuePickerMobile({
  production,
  installedPower,
  powerLevels,
  selectValue,
}: Readonly<ValuePickerMobileProps>): ReactElement<ValuePickerMobileProps> {
  const [t] = useTranslation();

  const rangeRef = useRef<HTMLInputElement>(null);

  const setValue = (value: number) => selectValue({ value, unit: 'kW' });

  const updateStepperValue = (step: number) => () => {
    if (!rangeRef.current) return;

    const newValue = production + step > installedPower ? installedPower : production + step;

    if (!powerLevels?.length) {
      setValue(newValue >= 0 ? newValue : 0);
      rangeRef.current.value = String(newValue >= 0 ? newValue : 0);
    } else if (powerLevels.length === 1) {
      if (step < 0 && newValue < powerLevels[0]) {
        setValue(0);
        rangeRef.current.value = String(0);
      } else if (step > 0 && newValue < powerLevels[0]) {
        setValue(powerLevels[0]);
        rangeRef.current.value = String(powerLevels[0]);
      } else {
        setValue(newValue);
        rangeRef.current.value = String(newValue);
      }
    } else {
      const alignedValue = alignToPowerLevel(newValue, powerLevels, installedPower, step);
      setValue(alignedValue);
      rangeRef.current.value = String(alignedValue);
    }
  };

  const updateRangeValue = () => {
    if (!rangeRef.current) return;

    const newValue = rangeRef.current.valueAsNumber;

    if (!powerLevels?.length) setValue(newValue);
    else if (powerLevels.length === 1) {
      if (newValue < powerLevels[0] / 2) {
        setValue(0);
        rangeRef.current.value = String(0);
      } else if (newValue < powerLevels[0]) {
        setValue(powerLevels[0]);
        rangeRef.current.value = String(powerLevels[0]);
      } else setValue(newValue);
    } else {
      const alignedValue = alignToPowerLevel(newValue, powerLevels, installedPower);
      setValue(alignedValue);
      rangeRef.current.value = String(alignedValue);
    }
  };

  const stepperStyle = 'appearance-none bg-transparent border-none px-2 py-4 m-0 active:bg-black/5 transition-colors';

  return (
    <div className="mt-4 flex flex-col items-center gap-4">
      <div className="flex w-3/4 max-w-64 items-center">
        <div className="box-border flex items-center rounded-l-lg bg-green-100">
          <button
            className={stepperStyle}
            title={t('FORECAST_ASSISTANT_FORECAST_FIFTY_KILOWATT_LESS')}
            onClick={updateStepperValue(-50)}
          >
            <div className="size-6 bg-green-300/80 [clip-path:polygon(20%_40%,_80%_40%,_80%_60%,_20%_60%)]" />
          </button>
          {(!powerLevels || powerLevels.length <= 1) && (
            <>
              <div className="h-4 w-px bg-green-200" />
              <button
                className={stepperStyle}
                title={t('FORECAST_ASSISTANT_FORECAST_ONE_KILOWATT_LESS')}
                onClick={updateStepperValue(-1)}
              >
                <div className="my-1 size-4 bg-green-200 [clip-path:polygon(20%_40%,_80%_40%,_80%_60%,_20%_60%)]" />
              </button>
            </>
          )}
        </div>
        <div className="h-13 relative box-content flex grow items-center justify-center px-2 text-2xl leading-none text-green-200">
          <output className="flex items-baseline gap-1">
            {production}
            <span className="text-base leading-none">kW</span>
          </output>
        </div>
        <div className="box-border flex items-center rounded-r-lg bg-green-100">
          {(!powerLevels || powerLevels.length <= 1) && (
            <>
              <button
                className={stepperStyle}
                title={t('FORECAST_ASSISTANT_FORECAST_ONE_KILOWATT_MORE')}
                onClick={updateStepperValue(1)}
              >
                <div className="my-1 size-4 bg-green-200 [clip-path:polygon(20%_40%,_40%_40%,_40%_20%,_60%_20%,_60%_40%,_80%_40%,_80%_60%,_60%_60%,_60%_80%,_40%_80%,_40%_60%,_20%_60%)]" />
              </button>
              <div className="h-4 w-px bg-green-200" />
            </>
          )}
          <button
            className={stepperStyle}
            title={t('FORECAST_ASSISTANT_FORECAST_FIFTY_KILOWATT_MORE')}
            onClick={updateStepperValue(50)}
          >
            <div className="size-6 bg-green-300/80 [clip-path:polygon(20%_40%,_40%_40%,_40%_20%,_60%_20%,_60%_40%,_80%_40%,_80%_60%,_60%_60%,_60%_80%,_40%_80%,_40%_60%,_20%_60%)]" />
          </button>
        </div>
      </div>
      <div className="flex w-4/5 max-w-72 flex-col items-center">
        <input
          ref={rangeRef}
          type="range"
          className="w-full accent-green-200"
          min={0}
          max={installedPower}
          value={production}
          list="powerLevels"
          onChange={updateRangeValue}
        />
        <datalist id="powerLevels">
          {[0, ...(powerLevels ?? []), installedPower].map((powerLevel) => (
            <option key={powerLevel}>{powerLevel}</option>
          ))}
        </datalist>
        <div className="h-5 w-full text-sm [container:production_/_size]">
          {[0, ...(powerLevels ?? []), installedPower].map((powerLevel, index) => (
            <div
              key={powerLevel}
              className="relative -left-4 w-8 text-center"
              style={{
                transform: `translate(${(powerLevel / installedPower) * 100}cqw, ${index * -100}%)`,
              }}
            >
              {powerLevel}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
